import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'

import { CreateOrgPayload } from 'api/types/orgsApiTypes'
import useApi from 'api/useApi'
import {
  OrgMetadata,
  OrgStatus,
  OrgType,
} from 'components/admin-navbar/org-select/OrgMetadataSelect'
import { useOrgQueryKey } from 'hooks/utils/useOrgQueryKey'
import { isZeusEnvironment } from 'utils/environment'

import { QueryKey } from './queryKeys'

export const useCreateOrg = () => {
  const { create_org } = useApi()
  const queryClient = useQueryClient()

  const shouldEnableLegalEntities = isZeusEnvironment()

  return useMutation(
    [QueryKey.OrgsByParent],
    (payload: CreateOrgPayload) =>
      create_org({
        ...payload,
        settings: {
          ...payload.settings,
          ...(shouldEnableLegalEntities && { enable_legal_entities: true }),
        },
        metadata: {
          ...payload.metadata,
          ...(isZeusEnvironment() && {
            type: OrgType.Customer,
            status: OrgStatus.Active,
          }),
        },
      }),
    {
      onSuccess: () => queryClient.invalidateQueries([QueryKey.OrgsByParent]),
    }
  )
}

export const useGetOrgMetadata = () => {
  const { fetch_org_metadata } = useApi()

  const orgMetadataQueryKey = useOrgQueryKey(QueryKey.OrgMetadata)

  return useQuery(orgMetadataQueryKey, fetch_org_metadata)
}

export const useUpdateOrgMetadata = () => {
  const { update_org_metadata } = useApi()
  const queryClient = useQueryClient()

  return useMutation((metadata: OrgMetadata) => update_org_metadata(metadata), {
    onSuccess: async () => {
      await queryClient.invalidateQueries([QueryKey.OrgMetadata])
    },
  })
}

export const useOrgsByParent = () => {
  const { fetch_all_orgs_by_parent } = useApi()
  const orgsByParentQueryKey = useOrgQueryKey(QueryKey.OrgsByParent)

  return useQuery(orgsByParentQueryKey, fetch_all_orgs_by_parent)
}
